import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDisclosure, useColorModeValue } from '@chakra-ui/react';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { useToast } from '@/client/hooks/useToast';
import { useProfile, useProfileAnalysis, useDeleteProfile, useProfiles, useCompareProfiles } from '@/lib/sdk/profiling/queries';
import { ProfileStatus } from '@/lib/sdk/profiling/types';

export function useProfileDetailPage() {
  const { profileId, orgId } = useParams<{ profileId: string; orgId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [compareProfileId, setCompareProfileId] = useState('');

  const { data: profile, isLoading: isLoadingProfile } = useProfile(profileId || '');
  const { data: analysis, isLoading: isLoadingAnalysis } = useProfileAnalysis(profileId || '');
  const { data: allProfiles } = useProfiles({ limit: 50 });
  const { mutate: deleteProfile, isPending: isDeleting } = useDeleteProfile();
  const { mutate: compareProfiles, data: comparisonData, isPending: isComparing } = useCompareProfiles();

  const cardGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );

  const isSuperAdmin = user?.is_super_admin === true;

  const compareableProfiles = allProfiles?.items?.filter(
    p => p.id !== profile?.id && p.profile_type === profile?.profile_type && p.status === ProfileStatus.COMPLETED
  ) ?? [];

  const handleDelete = () => {
    if (!profile) return;
    deleteProfile(profile.id, {
      onSuccess: () => { toast.success('Profile deleted successfully'); navigate(`/dashboard/${orgId}/profiling/profiles`); },
      onError: (error: Error) => toast.error(error.message || 'Failed to delete profile'),
    });
  };

  const handleCompare = () => {
    if (!compareProfileId || !profile) return;
    compareProfiles({ base_profile_id: profile.id, compare_profile_id: compareProfileId });
  };

  return {
    orgId, navigate, isSuperAdmin,
    profile, isLoadingProfile,
    analysis, isLoadingAnalysis,
    compareableProfiles, comparisonData, isComparing,
    isDeleting,
    isDeleteOpen, onDeleteOpen, onDeleteClose,
    compareProfileId, setCompareProfileId,
    cardGradient,
    handleDelete, handleCompare,
  };
}
